// src/features/wf2/popup/W2FichaModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import s from "./W2FichaModal.module.css";
import api from "../../../api/axios";
import { on as rtOn, off as rtOff, RT } from "../../../api/realtime";
import { getW2ById, programarW2, negarW2 } from "../api";
import { salvarOuEditarW2FromPayload } from "./W2Salvar";
import W2Editar from "./W2Editar";
import W2Negar from "./W2Negar";
import W2ProgramarModal from "./W2ProgramarModal";
import W2DateErrorModal from "./W2DateErrorModal.jsx";

/* Bases do WF-2 (mesmas do antigo W2Editar) */
import {
  getGerencias,
  getCoordenadores,
  getSubs,
  getSbs,
  validarKm,
  sbKm,
  getAtivosNomes,
  getAtividadesNomes,
} from "../../execucao/api";

/* ----------------- helpers básicos ----------------- */
const norm = (v) => (v == null ? "" : String(v));
const trim = (v) => norm(v).trim();

function toHHMM(raw) {
  const sv = trim(raw);
  if (!sv) return "";
  let m = sv.match(/^(\d{1,2}):([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/^(\d{1,2}):([0-5]\d):([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/^(\d{1,2})([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/(\d{1,2}):([0-5]\d)/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "";
}

function sanitizeQuantidade(raw) {
  const s = trim(raw);
  if (!s) return "";
  const m = s.match(/^(-?\d+)([.,]\d+)?$/);
  if (!m) return s;
  const inteiro = m[1];
  let frac = m[2] || "";
  if (frac) {
    frac = frac.replace(",", ".").replace(/0+$/, "");
    if (frac === "." || frac === "") frac = "";
  }
  return inteiro + (frac || "");
}

function normalizePrioridade(v) {
  const u = trim(v).toUpperCase();
  if (!u) return "";
  if (/^P[0-5]$/.test(u)) return u;
  if (u === "ALTA") return "P1";
  if (u === "MEDIA" || u === "MÉDIA") return "P3";
  if (u === "BAIXA") return "P5";
  return "";
}

function intOrEmpty(v) {
  const t = trim(v);
  if (!t) return "";
  const n = Number(t);
  if (!Number.isFinite(n)) return "";
  const i = Math.trunc(n);
  return i < 0 ? "" : String(i);
}

const cleanDec = (raw) =>
  String(raw ?? "").replace(",", ".").replace(/[^\d.-]/g, "");

/* auxiliares para combos */
const keyify = (s) => String(s || "").trim().toUpperCase();
const pickArray = (resp) =>
  Array.isArray(resp)
    ? resp
    : Array.isArray(resp?.data)
    ? resp.data
    : Array.isArray(resp?.data?.data)
    ? resp.data.data
    : [];

const unitFromRow = (row) =>
  row?.unidade ??
  row?.unidade_medida ??
  row?.medida ??
  row?.unid ??
  row?.unidade_de_medida ??
  "";

const normalizeSbItem = (x) =>
  x && typeof x === "object"
    ? String(x.sb ?? x.patio ?? x.nome ?? x.label ?? "").trim()
    : String(x ?? "").trim();

const uniqSorted = (arr) =>
  Array.from(new Set(arr.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "pt-BR")
  );

function createStaticBasesCache() {
  return {
    gerencias: null,
    atividades: null,
    ativos: null,
    sbsAll: null,
  };
}

/* ==== helper comum: patch a partir do form ==== */
function buildPatchFromForm(form) {
  if (!form) return {};
  return {
    ...(toHHMM(form.horario_do_intervalo)
      ? { horario_do_intervalo: toHHMM(form.horario_do_intervalo) }
      : {}),
    ...(toHHMM(form.horario_inicio_jornada_turma)
      ? {
          horario_inicio_jornada_turma: toHHMM(
            form.horario_inicio_jornada_turma
          ),
        }
      : {}),
    ...(toHHMM(form.tempo_do_intervalo)
      ? { tempo_do_intervalo: toHHMM(form.tempo_do_intervalo) }
      : {}),
    ...(intOrEmpty(form.quantidade_maquinista)
      ? { quantidade_maquinista: intOrEmpty(form.quantidade_maquinista) }
      : {}),
    ...(trim(form.maquinista) ? { maquinista: trim(form.maquinista) } : {}),
    ...(trim(form.tipo_acionamento)
      ? { tipo_acionamento: trim(form.tipo_acionamento) }
      : {}),
    ...(trim(form.locomotiva1) ? { locomotiva1: trim(form.locomotiva1) } : {}),
    ...(trim(form.locomotiva2) ? { locomotiva2: trim(form.locomotiva2) } : {}),
    ...(trim(form.tipo_programacao)
      ? { tipo_programacao: trim(form.tipo_programacao) }
      : {}),
    ...(normalizePrioridade(form.prioridade)
      ? { prioridade: normalizePrioridade(form.prioridade) }
      : {}),
    ...(trim(form.responsavel_pelo_aceite)
      ? { responsavel_pelo_aceite: trim(form.responsavel_pelo_aceite) }
      : {}),
    ...(trim(form.observacao) ? { observacao: trim(form.observacao) } : {}),
    ...(trim(form.gerencia) ? { gerencia: trim(form.gerencia) } : {}),
    ...(sanitizeQuantidade(form.quantidade)
      ? { quantidade: sanitizeQuantidade(form.quantidade) }
      : {}),
    ...(trim(form.unidade_de_medida)
      ? { unidade_de_medida: trim(form.unidade_de_medida) }
      : {}),
    ...(trim(form.os) ? { os: trim(form.os) } : {}),
  };
}

/* ----------------- Componente ----------------- */
export default function W2FichaModal({
  open = false,
  data = {},
  onClose = () => {},
  onSave,
  onProgramar,
  onNegar,
}) {
  /* ===== listas para combos (bases) ===== */
  const [gerencias, setGerencias] = useState([]);
  const [coordenadores, setCoordenadores] = useState([]);
  const [subs, setSubs] = useState([]);
  const [sbsAll, setSbsAll] = useState([]);
  const [sbsCoord, setSbsCoord] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [ativos, setAtivos] = useState([]);

  /* Popup de edição completa (W2Editar.jsx) */
  const [openEditar, setOpenEditar] = useState(false);

  /* Popup JUSTIFICATIVA para NEGAR (componente W2Negar) */
  const [openNegar, setOpenNegar] = useState(false);

  /* Popup PROGRAMAR (W2ProgramarModal) */
  const [openProgramar, setOpenProgramar] = useState(false);

  const staticBasesCacheRef = useRef(createStaticBasesCache());
  const coordenadoresCacheRef = useRef(new Map());
  const coordDepsCacheRef = useRef(new Map());
  const refreshTimerRef = useRef(null);
  const refreshInFlightRef = useRef(null);

  const [dateError, setDateError] = useState("");

  /* Form principal da ficha (exibição) */
  const [form, setForm] = useState(() => ({
    // ✅ status_wf2 (pra travar o botão e evitar re-programação)
    status_wf2: trim(data.status_wf2 || data.status || ""),

    gerencia: trim(data.gerencia || ""),
    coordenador: trim(data.coordenador || ""),
    atividade: trim(data.atividade || ""),
    ativo: trim(data.ativo || ""),
    quantidade: sanitizeQuantidade(data.quantidade ?? data.qtd ?? ""),
    unidade_de_medida: trim(data.unidade_de_medida ?? data.medida ?? ""),
    os: trim(data.os || ""),
    observacao: trim(data.observacao || ""),

    sub: trim(data.sub || ""),
    kmInicio: trim(data.kmInicio || ""),
    kmFinal: trim(data.kmFinal || ""),
    sbOrigem: trim(data.sbOrigem || ""),
    sbDeTrabalho: trim(data.sbDeTrabalho || ""),
    sbEncerramento: trim(data.sbEncerramento || ""),

    nome_do_responsavel: trim(data.nome_do_responsavel || ""),
    telefone_do_responsavel: trim(
      data.telefone_do_responsavel ||
        data.telefone_do_reponsavel ||
        data.telefone_responsavel ||
        ""
    ),
    autor_programacao: trim(
      data.autor_programacao ||
        data.usuario_alteracao ||
        data.usuario_responsavel ||
        ""
    ),

    horario_do_intervalo: toHHMM(
      data.horario_do_intervalo || data.horario || data.hora || ""
    ),
    horario_inicio_jornada_turma: toHHMM(
      data.horario_inicio_jornada_turma || ""
    ),
    tempo_do_intervalo: toHHMM(data.tempo_do_intervalo || ""),

    quantidade_maquinista: trim(data.quantidade_maquinista || ""),
    maquinista: trim(data.maquinista || ""),
    locomotiva1: trim(data.locomotiva1 || ""),
    locomotiva2: trim(data.locomotiva2 || ""),
    tipo_acionamento: trim(data.tipo_acionamento || ""),
    tipo_programacao: trim(data.tipo_programacao || ""),
    prioridade: trim(data.prioridade || ""),
    responsavel_pelo_aceite: trim(data.responsavel_pelo_aceite || ""),

    kmValido: true,
    sbMsg: "",
  }));

  const initialFormRef = useRef(form);

  // ✅ flag derivada do status (pode vir do backend no refresh)
  const isProgramado = useMemo(() => {
    const st =
      String(form.status_wf2 || data.status_wf2 || data.status || "")
        .trim()
        .toUpperCase();
    return st === "PROGRAMADO";
  }, [form.status_wf2, data.status_wf2, data.status]);

  /* índices (usados para validar, mas não mudam UI) */
  const atividadeIndex = useMemo(() => {
    const map = new Map();
    (atividades || []).forEach((row) => {
      const nome = String(
        row?.atividade || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k))
        map.set(k, { atividade: nome, unidade: unitFromRow(row) });
    });
    return map;
  }, [atividades]);

  const ativoIndex = useMemo(() => {
    const map = new Map();
    (ativos || []).forEach((row) => {
      const nome = String(
        row?.ativos || row?.ativo || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, { ativos: nome });
    });
    return map;
  }, [ativos]);

  const atividadesOptions = useMemo(() => {
    const set = new Set(
      (atividades || [])
        .map((r) => String(r?.atividade || r || ""))
        .filter(Boolean)
    );
    if (form.atividade) set.add(form.atividade);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [atividades, form.atividade]);

  const ativosOptions = useMemo(() => {
    const set = new Set(
      (ativos || [])
        .map((r) => String(r?.ativos || r?.ativo || r || ""))
        .filter(Boolean)
    );
    if (form.ativo) set.add(form.ativo);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [ativos, form.ativo]);

  const subsIds = useMemo(
    () => subs.map((s) => s.subdivisao_id ?? s.sub ?? s.id).filter(Boolean),
    [subs]
  );

  const atividadeValida =
    !!(form.atividade && atividadeIndex.has(keyify(form.atividade)));
  const ativoValido =
    !form.ativo || ativoIndex.has(keyify(form.ativo)) || !atividadeValida
      ? true
      : false;

  /* ===== Carrega bases quando abre (para valid. KM etc) ===== */
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    (async () => {
      const cache = staticBasesCacheRef.current;
      try {
        if (!cache.gerencias) cache.gerencias = (await getGerencias()) || [];
        setGerencias(cache.gerencias || []);
      } catch {
        setGerencias([]);
      }

      try {
        if (!cache.atividades) {
          const r = await api.get("/bases/atividades/listar", {
            params: { limit: 100000 },
          });
          cache.atividades = pickArray(r);
        }
        setAtividades(cache.atividades || []);
      } catch {
        try {
          if (!cache.atividades) {
            cache.atividades = pickArray(await getAtividadesNomes());
          }
          setAtividades(cache.atividades || []);
        } catch {
          setAtividades([]);
        }
      }

      try {
        if (!cache.ativos) {
          const r = await api.get("/bases/ativos/listar", {
            params: { limit: 100000 },
          });
          cache.ativos = pickArray(r);
        }
        setAtivos(cache.ativos || []);
      } catch {
        try {
          if (!cache.ativos) {
            cache.ativos = pickArray(await getAtivosNomes());
          }
          setAtivos(cache.ativos || []);
        } catch {
          setAtivos([]);
        }
      }

      let all = cache.sbsAll;
      if (!all) {
        const tryUrls = [
          "/bases/sb-origem-encerramento/listar",
          "/sb-origem-encerramento/listar",
        ];
        all = [];
        for (const url of tryUrls) {
          try {
            const resp = await api.get(url, { params: { limit: 100000 } });
            all = all.concat(pickArray(resp).map(normalizeSbItem));
          } catch {}
        }
        cache.sbsAll = uniqSorted(all);
      }

      // mantém a lista carregada (para validação/uso em outras telas),
      // MAS NÃO preenche automaticamente os campos na ficha.
      all = uniqSorted([
        ...all,
        normalizeSbItem(data.sbOrigem),
        normalizeSbItem(data.sbEncerramento),
      ]);
      setSbsAll(all);
    })();
  }, [open, data.sbOrigem, data.sbEncerramento]);

  /* Gerência -> Coordenadores */
  const prevGer = useRef(form.gerencia);
  useEffect(() => {
    if (!open) return;

    const changed = prevGer.current !== form.gerencia;
    prevGer.current = form.gerencia;

    if (!form.gerencia) {
      setCoordenadores([]);
      return;
    }
    const cached = coordenadoresCacheRef.current.get(form.gerencia);
    if (cached) {
      setCoordenadores(cached);
    } else {
      getCoordenadores(form.gerencia)
        .then((l) => {
          const next = l || [];
          coordenadoresCacheRef.current.set(form.gerencia, next);
          setCoordenadores(next);
        })
        .catch(() => setCoordenadores([]));
    }

    if (changed) {
      setSubs([]);
      setSbsCoord([]);
    }
  }, [open, form.gerencia]);

  /* Coordenador -> SUBs + SBs */
  const prevCoord = useRef(form.coordenador);
  useEffect(() => {
    if (!open) return;

    const changed = prevCoord.current !== form.coordenador;
    prevCoord.current = form.coordenador;

    if (!form.coordenador) {
      setSubs([]);
      setSbsCoord([]);
      return;
    }

    const cached = coordDepsCacheRef.current.get(form.coordenador);
    if (cached) {
      setSubs(cached.subs || []);
      setSbsCoord(cached.sbs || []);
    } else {
      Promise.all([getSubs(form.coordenador), getSbs(form.coordenador)])
        .then(([subsList, sbsList]) => {
          const next = {
            subs: subsList || [],
            sbs: uniqSorted((sbsList || []).map(normalizeSbItem)),
          };
          coordDepsCacheRef.current.set(form.coordenador, next);
          setSubs(next.subs);
          setSbsCoord(next.sbs);
        })
        .catch(() => {
          setSubs([]);
          setSbsCoord([]);
        });
    }

    if (changed) {
      // nada
    }
  }, [open, form.coordenador]);

  /**
   * ✅ FIX DO “BUG”:
   * Removido o prefill automático de sbOrigem/sbEncerramento com sbsAll[0].
   * Agora a ficha SEMPRE reflete o que vem do banco:
   * - se vier vazio => continua vazio
   * - se vier preenchido => mostra o valor real
   */

  /* SUB + KM -> valida KM / SB */
  const prevTriad = useRef("");
  useEffect(() => {
    if (!open) return;

    const sub = form.sub;
    const ki = Number(cleanDec(form.kmInicio));
    const kf = Number(cleanDec(form.kmFinal));
    const key = [
      sub || "",
      Number.isNaN(ki) ? "" : ki,
      Number.isNaN(kf) ? "" : kf,
    ].join("|");
    if (prevTriad.current === key) return;
    prevTriad.current = key;

    if (sub && Number.isFinite(ki) && Number.isFinite(kf)) {
      Promise.all([validarKm(sub, ki, kf), sbKm(sub, ki, kf)])
        .then(([val, sbsList]) => {
          const arr = (Array.isArray(sbsList) ? sbsList : [])
            .map(normalizeSbItem)
            .filter(Boolean);
          setForm((f) => ({
            ...f,
            kmValido: !!val?.existe,
            sbDeTrabalho: f.sbDeTrabalho,
            sbMsg: val?.existe
              ? arr.length
                ? ""
                : "Não há SB para o intervalo informado."
              : "KM fora da SUB.",
          }));
        })
        .catch(() =>
          setForm((f) => ({
            ...f,
            kmValido: false,
            sbMsg: "Erro ao buscar KM.",
          }))
        );
    } else {
      setForm((f) => ({
        ...f,
        kmValido: true,
        sbMsg: "",
      }));
    }
  }, [open, form.sub, form.kmInicio, form.kmFinal]);

  /* ===== refresh direto do backend (sem F5) ===== */
  const refreshFromBackend = React.useCallback(async () => {
    const id = data?.id;
    if (!id) return;
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    refreshInFlightRef.current = (async () => {
      try {
        const row = await getW2ById(id);
        if (!row) return;

        const next = {
          status_wf2: trim(row.status_wf2 || row.status || ""),

        gerencia: trim(row.gerencia || ""),
        coordenador: trim(row.coordenador || ""),
        atividade: trim(row.atividade || ""),
        ativo: trim(row.ativo || ""),
        quantidade: sanitizeQuantidade(row.quantidade ?? row.qtd ?? ""),
        unidade_de_medida: trim(row.unidade_de_medida ?? row.medida ?? ""),
        os: trim(row.os || ""),
        observacao: trim(row.observacao || ""),

        sub: trim(row.sub || ""),
        kmInicio: trim(row.kmInicio || ""),
        kmFinal: trim(row.kmFinal || ""),
        sbOrigem: trim(row.sbOrigem || ""),
        sbDeTrabalho: trim(row.sbDeTrabalho || ""),
        sbEncerramento: trim(row.sbEncerramento || ""),

        nome_do_responsavel: trim(row.nome_do_responsavel || ""),
        telefone_do_responsavel: trim(
          row.telefone_do_responsavel ||
            row.telefone_do_reponsavel ||
            row.telefone_responsavel ||
            ""
        ),
        autor_programacao: trim(
          row.autor_programacao ||
            row.usuario_alteracao ||
            row.usuario_responsavel ||
            ""
        ),

        horario_do_intervalo: toHHMM(
          row.horario_do_intervalo || row.horario || row.hora || ""
        ),
        horario_inicio_jornada_turma: toHHMM(
          row.horario_inicio_jornada_turma || ""
        ),
        tempo_do_intervalo: toHHMM(row.tempo_do_intervalo || ""),

        quantidade_maquinista: trim(row.quantidade_maquinista || ""),
        maquinista: trim(row.maquinista || ""),
        locomotiva1: trim(row.locomotiva1 || ""),
        locomotiva2: trim(row.locomotiva2 || ""),
        tipo_acionamento: trim(row.tipo_acionamento || ""),
        tipo_programacao: trim(row.tipo_programacao || ""),
        prioridade: trim(row.prioridade || ""),
        responsavel_pelo_aceite: trim(row.responsavel_pelo_aceite || ""),

        kmValido: true,
        sbMsg: "",
      };

        setForm(next);
        initialFormRef.current = next;
      } catch {
        // silencioso
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    return refreshInFlightRef.current;
  }, [data?.id]);

  const scheduleRefreshFromBackend = React.useCallback(
    (delay = 120) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        refreshFromBackend();
      }, delay);
    },
    [refreshFromBackend]
  );

  // sincroniza quando abre / muda data
  useEffect(() => {
    const prime = {
      ...initialFormRef.current,

      status_wf2: trim(data.status_wf2 || data.status || ""),

      gerencia: trim(data.gerencia || ""),
      coordenador: trim(data.coordenador || ""),
      atividade: trim(data.atividade || ""),
      ativo: trim(data.ativo || ""),
      quantidade: sanitizeQuantidade(data.quantidade ?? data.qtd ?? ""),
      unidade_de_medida: trim(data.unidade_de_medida ?? data.medida ?? ""),
      os: trim(data.os || ""),
      observacao: trim(data.observacao || ""),

      sub: trim(data.sub || ""),
      kmInicio: trim(data.kmInicio || ""),
      kmFinal: trim(data.kmFinal || ""),
      sbOrigem: trim(data.sbOrigem || ""),
      sbDeTrabalho: trim(data.sbDeTrabalho || ""),
      sbEncerramento: trim(data.sbEncerramento || ""),

      nome_do_responsavel: trim(data.nome_do_responsavel || ""),
      telefone_do_responsavel: trim(
        data.telefone_do_responsavel ||
          data.telefone_do_reponsavel ||
          data.telefone_responsavel ||
          ""
      ),
      autor_programacao: trim(
        data.autor_programacao ||
          data.usuario_alteracao ||
          data.usuario_responsavel ||
          ""
      ),

      horario_do_intervalo: toHHMM(
        data.horario_do_intervalo || data.horario || data.hora || ""
      ),
      horario_inicio_jornada_turma: toHHMM(
        data.horario_inicio_jornada_turma || ""
      ),
      tempo_do_intervalo: toHHMM(data.tempo_do_intervalo || ""),

      quantidade_maquinista: trim(data.quantidade_maquinista || ""),
      maquinista: trim(data.maquinista || ""),
      locomotiva1: trim(data.locomotiva1 || ""),
      locomotiva2: trim(data.locomotiva2 || ""),
      tipo_acionamento: trim(data.tipo_acionamento || ""),
      tipo_programacao: trim(data.tipo_programacao || ""),
      prioridade: trim(data.prioridade || ""),
      responsavel_pelo_aceite: trim(data.responsavel_pelo_aceite || ""),

      kmValido: true,
      sbMsg: "",
    };

    setForm(prime);
    initialFormRef.current = prime;

    if (open && data?.id) {
      scheduleRefreshFromBackend();
    }
  }, [open, data, scheduleRefreshFromBackend]);

  // ===== Realtime: se chegar evento do mesmo codigo_unico, refaz GET =====
  useEffect(() => {
    if (!open) return;
    const handler = (evt) => {
      try {
        const cuEvt =
          evt?.codigo_unico ??
          evt?.codigo ??
          evt?.payload?.codigo_unico ??
          evt?.data?.codigo_unico ??
          null;
        const cuLocal = data.codigo_unico ?? data.id ?? null;
        if (!cuEvt || !cuLocal || String(cuEvt) !== String(cuLocal)) return;
        scheduleRefreshFromBackend();
      } catch (_) {}
    };
    rtOn(RT.UPDATED, handler);
    rtOn(RT.MOVED, handler);
    rtOn(RT.CREATED, handler);
    return () => {
      rtOff(RT.UPDATED, handler);
      rtOff(RT.MOVED, handler);
      rtOff(RT.CREATED, handler);
    };
  }, [open, data, scheduleRefreshFromBackend]);

  const setF = (k) => (e) => {
    const raw = e?.target?.value ?? e;
    let v = raw;
    if (
      k === "horario_do_intervalo" ||
      k === "tempo_do_intervalo" ||
      k === "horario_inicio_jornada_turma"
    )
      v = toHHMM(raw);
    if (k === "prioridade") v = normalizePrioridade(raw) || raw;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  /* ==== persistência básica ==== */

  // SALVAR (rascunho) — continua usando /wf_2/salvar
  async function salvarBackend(extras = {}) {
    const payload = {
      id: data.id ?? null,
      codigo_unico: data.codigo_unico ?? data.id ?? null,
      ...buildPatchFromForm(form),
      ...extras,
    };

    if (typeof onSave === "function" && extras.status_wf2 === undefined) {
      return onSave(payload);
    }

    return salvarOuEditarW2FromPayload(payload, "salvar");
  }

  // PROGRAMAR — NÃO chama mais /salvar antes
  async function programarBackend(extras = {}) {
    const payload = {
      id: data.id ?? null,
      codigo_unico: data.codigo_unico ?? data.id ?? null,
      ...buildPatchFromForm(form),
      ...extras,
    };

    if (typeof onProgramar === "function") {
      return onProgramar(payload);
    }

    return programarW2(payload);
  }

  // NEGAR — idem, manda patch + extras direto para /negar
  async function negarBackend(extras = {}) {
    const payload = {
      id: data.id ?? null,
      codigo_unico: data.codigo_unico ?? data.id ?? null,
      ...buildPatchFromForm(form),
      ...extras,
    };

    if (typeof onNegar === "function") {
      return onNegar(payload);
    }

    return negarW2(payload);
  }

  /* handlers botões */
  const handleSalvar = async () => {
    if (!atividadeValida) {
      alert("Selecione uma Atividade válida (dos combos do WF-2).");
      return;
    }
    try {
      await salvarBackend();
      document.dispatchEvent(
        new CustomEvent("wf2:updated", {
          detail: {
            id: data.id,
            codigo_unico: data.codigo_unico,
            kind: "salvar",
          },
        })
      );
      onClose?.();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Não foi possível salvar.";
      if (/passad/i.test(msg)) {
        setDateError("Data não pode ser no passado. Escolha uma data futura.");
      } else {
        alert("Não foi possível salvar.\n" + msg);
      }
    }
  };

  const handleProgramar = () => {
    if (isProgramado) {
      alert(
        "Essa programação já está PROGRAMADA. Se precisar mudar, use REPROGRAMAR/EDITAR."
      );
      return;
    }
    // bloqueia programar se a data original já passou
    const dataKey = String(data?.data || data?.data_programada || "")
      .trim()
      .slice(0, 10);
    const todaySp = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    if (dataKey && dataKey < todaySp) {
      setDateError("Data não pode ser no passado. Escolha uma data futura.");
      return;
    }
    setOpenProgramar(true);
  };

  const handleProgramarClose = async (ok /*, info */) => {
    setOpenProgramar(false);
    if (!ok) return;

    if (isProgramado) {
      alert("Essa programação já está PROGRAMADA.");
      scheduleRefreshFromBackend();
      return;
    }

    try {
      const extras = {
        justificativas_wf2: "(programação confirmada pela ficha)",
      };

      const out = await programarBackend(extras);

      document.dispatchEvent(
        new CustomEvent("wf2:updated", {
          detail: {
            id: data.id,
            codigo_unico: data.codigo_unico,
            kind: "programar",
            out,
          },
        })
      );
      onClose?.();
    } catch (e) {
      const st = e?.response?.status;
      if (st === 409) {
        alert("Já estava PROGRAMADO. Atualizando ficha…");
        scheduleRefreshFromBackend();
        return;
      }
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Não foi possível programar.";
      if (/passad/i.test(msg)) {
        setDateError("Data não pode ser no passado. Escolha uma data futura.");
      } else {
        alert("Não foi possível programar.\n" + msg);
      }
    }
  };

  const handleNegar = () => setOpenNegar(true);
  const handleNegarCancel = () => setOpenNegar(false);

  const handleNegarConfirm = async ({ causador, causa_primaria, reason }) => {
    try {
      const extras = {
        status_wf2: "NEGADO",
        justificativas_wf2: trim(reason) || "(sem justificativa informada)",
        causador: causador || null,
        causa_primaria: causa_primaria || null,
      };

      await negarBackend(extras);
      document.dispatchEvent(
        new CustomEvent("wf2:updated", {
          detail: {
            id: data.id,
            codigo_unico: data.codigo_unico,
            kind: "negar",
          },
        })
      );

      setOpenNegar(false);
      onClose?.();
    } catch (e) {
      alert("Não foi possível negar.\n" + (e?.message || e));
    }
  };

  const handleEditar = () => setOpenEditar(true);

  if (!open) return null;

  /* === UI === */
  return (
    <>
      <div className={s.backdrop}>
        <div className={`${s.modal} ${s.root}`}>
          <div className={s.header}>
            <div className={s.title}>Ficha da Programação</div>
            <button className={s.close} aria-label="Fechar" onClick={onClose}>
              ×
            </button>
          </div>

          <div className={s.content}>
            {/* ===== Dados Básicos ===== */}
            <section className={s.section}>
              <div className={s.sectionTitle}>Dados Básicos</div>

              <div className={`${s.grid} ${s.grid2}`}>
                <FieldDisplay label="Gerência" value={form.gerencia} />
                <FieldDisplay label="Coordenador" value={form.coordenador} />
              </div>

              <div className={`${s.grid} ${s.grid2}`}>
                <FieldDisplay label="Atividade" value={form.atividade} />
                <FieldDisplay label="Ativo" value={form.ativo} />
              </div>

              <div className={`${s.grid} ${s.grid3}`}>
                <FieldDisplay label="Quantidade" value={form.quantidade} />
                <FieldDisplay
                  label="Unidade de Medida"
                  value={form.unidade_de_medida}
                />
                <FieldDisplay label="OS" value={form.os} />
              </div>

              <div className={s.grid}>
                <FieldDisplay
                  className={s.span2}
                  label="Observação"
                  value={form.observacao}
                />
              </div>
            </section>

            {/* ===== Localização ===== */}
            <section className={s.section}>
              <div className={s.sectionTitle}>Localização</div>

              <div className={`${s.grid} ${s.grid3}`}>
                <FieldDisplay label="SUB" value={form.sub} />
                <FieldDisplay label="KM Início" value={form.kmInicio} />
                <FieldDisplay label="KM Fim" value={form.kmFinal} />
              </div>

              <div className={`${s.grid} ${s.grid3}`}>
                <FieldDisplay label="Pátio Origem" value={form.sbOrigem} />
                <FieldDisplay label="SB de Trabalho" value={form.sbDeTrabalho} />
                <FieldDisplay
                  label="Pátio Encerramento"
                  value={form.sbEncerramento}
                />
              </div>
              {form.sbMsg && <div className={s.helper}>{form.sbMsg}</div>}
            </section>

            {/* ===== Horário ===== */}
            <section className={s.section}>
              <div className={s.sectionTitle}>Horário</div>

              <div className={`${s.grid} ${s.grid3}`}>
                <FieldDisplay
                  label="Tempo do Intervalo"
                  value={form.tempo_do_intervalo}
                />
                <FieldEdit
                  label="Horário do Intervalo"
                  type="time"
                  step={60}
                  value={form.horario_do_intervalo}
                  onChange={setF("horario_do_intervalo")}
                  placeholder="HH:MM"
                  requiredHighlight={!trim(form.horario_do_intervalo)}
                />
                <FieldEdit
                  label="Horário de Início da Jornada da Turma"
                  type="time"
                  step={60}
                  value={form.horario_inicio_jornada_turma}
                  onChange={setF("horario_inicio_jornada_turma")}
                  placeholder="HH:MM"
                  requiredHighlight={!trim(form.horario_inicio_jornada_turma)}
                />
              </div>
            </section>

            {/* ===== Contato ===== */}
            <section className={s.section}>
              <div className={s.sectionTitle}>Contato</div>

              <div className={`${s.grid} ${s.grid3}`}>
                <FieldDisplay
                  label="Responsável pelo Ativo"
                  value={form.nome_do_responsavel}
                />
                <FieldDisplay
                  label="Telefone do Resp. pelo Ativo"
                  value={form.telefone_do_responsavel}
                />
                <FieldDisplay
                  label="Autor da Programação"
                  value={form.autor_programacao}
                />
              </div>
            </section>

            {/* ===== Aprovação (WF2) ===== */}
            <section className={s.section}>
              <div className={s.sectionTitle}>Aprovação</div>

              <div className={`${s.grid} ${s.grid2}`}>
                <FieldEdit
                  label="Quantidade de Maquinista"
                  value={form.quantidade_maquinista}
                  onChange={setF("quantidade_maquinista")}
                  placeholder="Ex.: 1"
                  optionalHighlight
                />
                <FieldSelect
                  label="Maquinista"
                  value={form.maquinista}
                  onChange={setF("maquinista")}
                  options={["ESCALA", "FIXO", "FIXO + ESCALA"]}
                  optionalHighlight
                />
              </div>

              <div className={`${s.grid} ${s.grid2}`}>
                <FieldEdit
                  label="Locomotiva 1"
                  value={form.locomotiva1}
                  onChange={setF("locomotiva1")}
                  placeholder="Identificação"
                  optionalHighlight
                />
                <FieldEdit
                  label="Locomotiva 2"
                  value={form.locomotiva2}
                  onChange={setF("locomotiva2")}
                  placeholder="Identificação"
                  optionalHighlight
                />
              </div>

              <div className={`${s.grid} ${s.grid2}`}>
                <FieldSelect
                  label="Tipo de Acionamento"
                  value={form.tipo_acionamento}
                  onChange={setF("tipo_acionamento")}
                  options={["SBR", "ACIONAMENTO", "AGUARDANDO ACIONAMENTO PCM"]}
                  optionalHighlight
                />
                <FieldSelect
                  label="Tipo de Programação"
                  value={form.tipo_programacao}
                  onChange={setF("tipo_programacao")}
                  options={["CONTRATO", "OPORTUNIDADE"]}
                  requiredHighlight={!trim(form.tipo_programacao)}
                />
              </div>

              <div className={`${s.grid} ${s.grid2}`}>
                <FieldSelect
                  label="Prioridade"
                  value={normalizePrioridade(form.prioridade)}
                  onChange={setF("prioridade")}
                  options={["P0", "P1", "P2", "P3", "P4", "P5"]}
                  placeholder="Selecione..."
                  requiredHighlight={!normalizePrioridade(form.prioridade)}
                />
                <FieldEdit
                  label="Responsável pelo Aceite"
                  value={form.responsavel_pelo_aceite}
                  onChange={setF("responsavel_pelo_aceite")}
                  placeholder="Nome do responsável pelo aceite"
                  requiredHighlight={!trim(form.responsavel_pelo_aceite)}
                />
              </div>
            </section>
          </div>

          {/* Footer / ações */}
          <div className={s.actions}>
            {isProgramado ? (
              <div className={s.actionsRight}>
                <span className={s.lockNotice}>somente leitura</span>
              </div>
            ) : (
              <div className={s.actionsRight}>
                <button
                  className={`${s.btn} ${s.btnGhost}`}
                  type="button"
                  onClick={handleEditar}
                >
                  Editar
                </button>
                <button
                  className={`${s.btn} ${s.btnDanger}`}
                  type="button"
                  onClick={handleNegar}
                >
                  Negar
                </button>
                <button className={s.btn} type="button" onClick={handleSalvar}>
                  Salvar
                </button>

                <button
                  className={`${s.btn} ${s.btnPrimary}`}
                  type="button"
                  onClick={handleProgramar}
                  disabled={isProgramado}
                  title={isProgramado ? "Já está PROGRAMADO" : "Programar"}
                >
                  {isProgramado ? "Programado" : "Programar"}
                </button>
              </div>
            )}
          </div>

          {/* Popup JUSTIFICATIVA para NEGAR */}
          <W2Negar
            open={openNegar}
            onCancel={handleNegarCancel}
            onConfirm={handleNegarConfirm}
          />
        </div>
      </div>

      {/* Popup PROGRAMAR */}
      <W2ProgramarModal
        open={openProgramar}
        codigo_unico={data.codigo_unico ?? data.id ?? ""}
        form={form}
        onClose={handleProgramarClose}
      />
      <W2DateErrorModal
        open={!!dateError}
        message={dateError}
        onClose={() => setDateError("")}
      />

      {/* Popup de edição completa (usa W2Editar.jsx) */}
      <W2Editar
        open={openEditar}
        data={data}
        onClose={() => setOpenEditar(false)}
        onSave={() => {
          scheduleRefreshFromBackend();
          setOpenEditar(false);
        }}
      />
    </>
  );
}

/* ---- inputs base ---- */
function FieldEdit({
  label,
  value,
  onChange,
  placeholder = "",
  className = "",
  requiredHighlight = false,
  optionalHighlight = false,
  type = "text",
  step,
}) {
  return (
    <label className={`${s.field} ${className || ""}`}>
      <span className={s.label}>{label}</span>
      <input
        type={type}
        step={step}
        className={`${s.input} ${
          requiredHighlight ? s.required : ""
        } ${optionalHighlight ? s.optional : ""}`}
        value={value ?? ""}
        onChange={(e) => onChange?.(e)}
        placeholder={placeholder}
      />
    </label>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  className = "",
  requiredHighlight = false,
  optionalHighlight = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const rootRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) =>
          typeof opt === "object"
            ? {
                value: String(opt?.value ?? opt?.label ?? "").trim(),
                label: String(opt?.label ?? opt?.value ?? "").trim(),
              }
            : {
                value: String(opt ?? "").trim(),
                label: String(opt ?? "").trim(),
              }
        )
        .filter((opt) => opt.value),
    [options]
  );

  const selectedOption = useMemo(
    () => normalizedOptions.find((opt) => opt.value === String(value ?? "")) || null,
    [normalizedOptions, value]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setOpenUp(false);
      return undefined;
    }

    const updateDirection = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;
      const rect = rootEl.getBoundingClientRect();
      const menuHeight = Math.min(Math.max(normalizedOptions.length * 40, 56), 220) + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [normalizedOptions.length, open]);

  const triggerClass = `${s.input} ${
    requiredHighlight ? s.required : ""
  } ${optionalHighlight ? s.optional : ""} ${s.selectTrigger}`.trim();

  const emitChange = (nextValue) => {
    onChange?.({ target: { value: nextValue } });
  };

  return (
    <label className={`${s.field} ${className || ""}`}>
      <span className={s.label}>{label}</span>
      <div
        ref={rootRef}
        className={`${s.selectRoot} ${open ? s.selectOpen : ""} ${
          openUp ? s.selectOpenUp : ""
        } ${
          disabled ? s.selectDisabled : ""
        }`.trim()}
      >
        <button
          type="button"
          className={triggerClass}
          onClick={() => {
            if (!disabled) setOpen((prev) => !prev);
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className={`${s.selectValue} ${
              selectedOption ? "" : s.selectPlaceholder
            }`.trim()}
          >
            {selectedOption?.label || placeholder}
          </span>
        </button>

        <div
          className={`${s.selectMenu} ${openUp ? s.selectMenuUp : ""}`.trim()}
          role="listbox"
          aria-hidden={!open}
        >
          {normalizedOptions.map((opt) => {
            const selected = selectedOption?.value === opt.value;
            return (
              <button
                type="button"
                key={opt.value}
                className={`${s.selectOption} ${
                  selected ? s.selectOptionSelected : ""
                }`.trim()}
                onClick={() => {
                  emitChange(opt.value);
                  setOpen(false);
                }}
                aria-selected={selected}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </label>
  );
}

/* Campo só de exibição (sem input/dropdown) */
function FieldDisplay({ label, value, className = "" }) {
  return (
    <div className={`${s.field} ${className || ""}`}>
      <span className={s.label}>{label}</span>
      <div className={`${s.input} ${s.display}`}>{value || ""}</div>
    </div>
  );
}
